import { EventBus } from '../core/EventBus';
import { createLogger } from '../utils/Logger';

export interface ExperienceConfig {
    baseXp: number;
    growthFactor: number;
}

export class ExperienceSystem {
    private currentLevel: number = 1;
    private currentXp: number = 0;
    private xpToNextLevel: number;
    private config: ExperienceConfig;
    private eventBus: EventBus;
    private awaitingUpgradeSelection: boolean = false;
    private readonly logger = createLogger('ExperienceSystem');
    private readonly eventUnsubscribers: Array<() => void> = [];
    private isDisposed = false;

    constructor(eventBus: EventBus, config: ExperienceConfig = { baseXp: 10, growthFactor: 1.5 }) {
        this.eventBus = eventBus;
        this.config = config;
        this.xpToNextLevel = this.calculateXpForLevel(this.currentLevel);

        this.subscribeEvents();
    }

    private subscribeEvents(): void {
        // Listen for enemy death to gain XP (if enemies give XP directly, 
        // though LootSystem might handle the pickup later)
        // For now, we'll assume XP comes from 'xp:gained' event which might be 
        // triggered by picking up a gem or direct kill if we skip loot for now.
        // However, the plan says LootSystem spawns gems. 
        // So this system should listen for 'xp:collected'.

        const onXpCollected = (event: any): void => {
            const amount = event.data;
            this.addXp(amount);
        };
        this.eventBus.on('xp:collected', onXpCollected);
        this.eventUnsubscribers.push(() => this.eventBus.off('xp:collected', onXpCollected));

        const onGameResume = (): void => {
            if (!this.awaitingUpgradeSelection) return;

            this.awaitingUpgradeSelection = false;
            this.logger.debug('resume:processing-deferred-level-up', {
                currentLevel: this.currentLevel,
                currentXp: this.currentXp,
                xpToNextLevel: this.xpToNextLevel
            });
            this.processNextLevelUp();
        };
        this.eventBus.on('game:resume', onGameResume);
        this.eventUnsubscribers.push(() => this.eventBus.off('game:resume', onGameResume));
    }

    public addXp(amount: number): void {
        if (!Number.isFinite(amount) || amount <= 0) {
            this.logger.warn('addXp:invalid-amount-ignored', { amount });
            return;
        }

        this.currentXp += amount;
        this.eventBus.publish({ type: 'xp:gained', data: { amount, current: this.currentXp, total: this.xpToNextLevel } });

        this.processNextLevelUp();
    }

    private processNextLevelUp(): void {
        if (this.awaitingUpgradeSelection || this.currentXp < this.xpToNextLevel) {
            return;
        }

        this.currentXp -= this.xpToNextLevel;
        this.currentLevel++;
        this.xpToNextLevel = this.calculateXpForLevel(this.currentLevel);
        this.awaitingUpgradeSelection = true;

        this.logger.debug('level-up:queued-upgrade-selection', {
            level: this.currentLevel,
            currentXp: this.currentXp,
            xpRequired: this.xpToNextLevel
        });

        this.eventBus.publish({
            type: 'level:up',
            data: {
                level: this.currentLevel,
                xpRequired: this.xpToNextLevel
            }
        });

        // Pause once per earned level so upgrade choices are resolved sequentially.
        this.eventBus.publish({ type: 'game:pause', data: { reason: 'levelup' } });
    }

    private calculateXpForLevel(level: number): number {
        return Math.floor(this.config.baseXp * Math.pow(this.config.growthFactor, level - 1));
    }

    public getLevel(): number {
        return this.currentLevel;
    }

    public getCurrentXp(): number {
        return this.currentXp;
    }

    public getXpToNextLevel(): number {
        return this.xpToNextLevel;
    }

    public dispose(): void {
        if (this.isDisposed) {
            this.logger.warn('dispose:already-disposed');
            return;
        }

        this.eventUnsubscribers.splice(0).forEach(unsubscribe => unsubscribe());
        this.isDisposed = true;
        this.logger.debug('dispose:complete');
    }
}
